import { useState, useRef } from 'react'
import {
  Plus,
  Zap,
  Pencil,
  Trash2,
  Eye,
  Heart,
  GripVertical,
  Upload,
  X,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TagInput } from '@/components/ui/tag-input'
import {
  useAdminDrops,
  useCreateDrop,
  useUpdateDrop,
  useDeleteDrop,
} from '@/services/admin/drops.service'
import type { Drop, CreateDropDTO, UpdateDropDTO } from '@/services/admin/drops.service'
import {
  useSuperAdminDrops,
  useSuperAdminCreateDrop,
  useSuperAdminUpdateDrop,
  useSuperAdminDeleteDrop,
} from '@/services/super-admin/drops.service'
import { useCompanies } from '@/services/super-admin/companies.service'
import { useUploadDropThumbnail } from '@/services/upload.service'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/hooks/use-auth'
import { canManageDrop } from '@/types/auth'
import { getErrorMessage } from '@/utils/error'

interface DropForm {
  title: string
  videoUrl: string
  thumbnailUrl: string
  tags: string[]
  status: 'draft' | 'published'
  companyId: string | null
  isGlobal: boolean
}

const emptyForm = (): DropForm => ({
  title: '',
  videoUrl: '',
  thumbnailUrl: '',
  tags: [],
  status: 'draft',
  companyId: null,
  isGlobal: true,
})

interface DropsManagementProps {
  isSuperAdmin?: boolean
}

export default function DropsManagement({ isSuperAdmin = false }: DropsManagementProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingDrop, setEditingDrop] = useState<Drop | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [form, setForm] = useState<DropForm>(emptyForm())
  const [thumbnailPreview, setThumbnailPreview] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()
  const { user } = useAuth()

  const { mutate: uploadThumbnail, isPending: isUploading } = useUploadDropThumbnail()

  // Hooks condicionais por role
  const adminDrops = useAdminDrops(!isSuperAdmin)
  const superAdminDrops = useSuperAdminDrops(isSuperAdmin)
  const companies = useCompanies(isSuperAdmin)

  const createDrop = useCreateDrop()
  const updateDrop = useUpdateDrop()
  const deleteDrop = useDeleteDrop()

  const superCreateDrop = useSuperAdminCreateDrop()
  const superUpdateDrop = useSuperAdminUpdateDrop()
  const superDeleteDrop = useSuperAdminDeleteDrop()

  const { data: drops, isLoading } = isSuperAdmin ? superAdminDrops : adminDrops

  const openCreate = () => {
    setEditingDrop(null)
    setForm(emptyForm())
    setThumbnailPreview('')
    setDialogOpen(true)
  }

  const openEdit = (drop: Drop) => {
    setEditingDrop(drop)
    setThumbnailPreview(drop.thumbnailUrl || '')
    setForm({
      title: drop.title,
      videoUrl: drop.videoUrl,
      thumbnailUrl: drop.thumbnailUrl || '',
      tags: drop.tags
        ? drop.tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : [],
      status: drop.status,
      companyId: drop.companyId || null,
      isGlobal: !drop.companyId,
    })
    setDialogOpen(true)
  }

  const handleCancel = () => {
    setForm(emptyForm())
    setEditingDrop(null)
    setThumbnailPreview('')
    if (fileInputRef.current) fileInputRef.current.value = ''
    setDialogOpen(false)
  }

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Preview local imediato
    const localUrl = URL.createObjectURL(file)
    setThumbnailPreview(localUrl)

    // Upload real via PHP
    uploadThumbnail(file, {
      onSuccess: (url) => {
        setForm((prev) => ({ ...prev, thumbnailUrl: url }))
        setThumbnailPreview(url)
        URL.revokeObjectURL(localUrl)
        toast({ title: 'Thumbnail enviada!' })
      },
      onError: (error: unknown) => {
        setThumbnailPreview('')
        setForm((prev) => ({ ...prev, thumbnailUrl: '' }))
        URL.revokeObjectURL(localUrl)
        toast({
          title: 'Erro ao enviar thumbnail',
          description: getErrorMessage(error),
          variant: 'destructive',
        })
      },
    })
  }

  const handleRemoveThumbnail = () => {
    setForm((prev) => ({ ...prev, thumbnailUrl: '' }))
    setThumbnailPreview('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmit = async () => {
    const tagsString = form.tags.length > 0 ? form.tags.join(', ') : undefined

    if (editingDrop) {
      const dto: UpdateDropDTO & { id: number } = {
        id: editingDrop.id,
        title: form.title,
        videoUrl: form.videoUrl,
        thumbnailUrl: form.thumbnailUrl || undefined,
        tags: tagsString,
        status: form.status,
      }

      if (isSuperAdmin) {
        await superUpdateDrop.mutateAsync({
          ...dto,
          companyId: form.isGlobal ? null : form.companyId,
        })
      } else {
        await updateDrop.mutateAsync(dto)
      }
    } else {
      const dto: CreateDropDTO = {
        title: form.title,
        videoUrl: form.videoUrl,
        thumbnailUrl: form.thumbnailUrl || undefined,
        tags: tagsString,
        status: form.status,
      }

      if (isSuperAdmin) {
        await superCreateDrop.mutateAsync({
          ...dto,
          companyId: form.isGlobal ? null : form.companyId,
        })
      } else {
        await createDrop.mutateAsync(dto)
      }
    }

    handleCancel()
  }

  const handleDelete = async (id: number) => {
    if (isSuperAdmin) {
      await superDeleteDrop.mutateAsync(id)
    } else {
      await deleteDrop.mutateAsync(id)
    }
    setDeleteConfirmId(null)
  }

  const isMutating =
    createDrop.isPending ||
    updateDrop.isPending ||
    superCreateDrop.isPending ||
    superUpdateDrop.isPending
  const isFormValid = form.title.trim() && form.videoUrl.trim()

  return (
    <>
      <Card className="glass-card border-border/50">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Drops
            </CardTitle>
            <CardDescription>Gerencie os microconteúdos em vídeo da plataforma</CardDescription>
          </div>
          <Button onClick={openCreate} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Novo Drop
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando drops...</p>
          ) : !drops || drops.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum drop cadastrado ainda.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Tags</TableHead>
                  {isSuperAdmin && <TableHead>Empresa</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">
                    <Eye className="w-4 h-4 inline" />
                  </TableHead>
                  <TableHead className="text-center">
                    <Heart className="w-4 h-4 inline" />
                  </TableHead>
                  <TableHead className="w-24">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drops.map((drop) => (
                  <TableRow key={drop.id}>
                    <TableCell className="text-muted-foreground">
                      <GripVertical className="w-4 h-4" />
                    </TableCell>
                    <TableCell className="font-medium">{drop.title}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {drop.tags
                          ?.split(',')
                          .map((t) => t.trim())
                          .filter(Boolean)
                          .map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                      </div>
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell className="text-sm text-muted-foreground">
                        {drop.company?.name || 'Global'}
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge variant={drop.status === 'published' ? 'default' : 'outline'}>
                        {drop.status === 'published' ? 'Publicado' : 'Rascunho'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">
                      {drop._count.drop_views}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">
                      {drop._count.drop_likes}
                    </TableCell>
                    <TableCell>
                      {canManageDrop(user?.role, user?.companyId, drop.companyId) ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(drop)}
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirmId(drop.id)}
                            title="Excluir"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">Somente leitura</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              {editingDrop ? 'Editar Drop' : 'Novo Drop'}
            </DialogTitle>
            <DialogDescription>
              {editingDrop
                ? 'Atualize as informações do drop.'
                : 'Preencha as informações do microconteúdo em vídeo.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="drop-title">Título</Label>
              <Input
                id="drop-title"
                placeholder="Ex: Como usar Chain-of-Thought no dia a dia"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="drop-video">Link do vídeo (YouTube Shorts)</Label>
              <Input
                id="drop-video"
                placeholder="https://youtube.com/shorts/..."
                value={form.videoUrl}
                onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Thumbnail (opcional)</Label>
              <p className="text-xs text-muted-foreground">
                Se não enviar, a capa do YouTube será usada automaticamente.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleThumbnailUpload}
              />
              {thumbnailPreview ? (
                <div className="relative w-full max-w-[200px]">
                  <div
                    className="relative rounded-lg overflow-hidden border border-border"
                    style={{ aspectRatio: '9/16' }}
                  >
                    <img
                      src={thumbnailPreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    {isUploading && (
                      <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveThumbnail}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-destructive rounded-full flex items-center justify-center text-white hover:bg-destructive/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-border/50 rounded-lg flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors cursor-pointer py-6"
                >
                  <Upload className="w-5 h-5" />
                  <span className="text-sm">Clique para fazer upload</span>
                  <span className="text-xs">JPG, PNG ou WEBP (máx. 10MB)</span>
                </button>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <TagInput
                value={form.tags}
                onChange={(tags) => setForm({ ...form, tags })}
                placeholder="Adicionar tag..."
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v: 'draft' | 'published') => setForm({ ...form, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Rascunho</SelectItem>
                  <SelectItem value="published">Publicado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isSuperAdmin && (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="drop-global"
                    checked={form.isGlobal}
                    onCheckedChange={(checked) =>
                      setForm({
                        ...form,
                        isGlobal: !!checked,
                        companyId: checked ? null : form.companyId,
                      })
                    }
                  />
                  <Label htmlFor="drop-global" className="cursor-pointer">
                    Global (disponível para todas as empresas)
                  </Label>
                </div>

                {!form.isGlobal && (
                  <div className="space-y-2">
                    <Label>Empresa</Label>
                    <Select
                      value={form.companyId || ''}
                      onValueChange={(v) => setForm({ ...form, companyId: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.data?.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={!isFormValid || isMutating}>
              {isMutating ? 'Salvando...' : editingDrop ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir Drop</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este drop? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={deleteDrop.isPending || superDeleteDrop.isPending}
            >
              {deleteDrop.isPending || superDeleteDrop.isPending ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
